import { FC, useEffect } from 'react'
import { Form, Input, Modal, Radio } from 'antd'
import { TMode } from 'types'
import { BaseButton } from 'components/elements/BaseButton'
import { FubData, TAddFubInput, TFubSyncDirection } from '../types'
import followupboss from '../images/followupboss.svg'
import { useFubConnectMutation, useFubUpdateMutation } from '../api'

const initialValues: TAddFubInput = {
 api_key: '',
 sync_direction: 'twoway',
}

const syncDirectionOptions: {
 value: TFubSyncDirection
 label: string
 description: string
 recommended?: boolean
}[] = [
 {
  value: 'twoway',
  label: 'Two-way sync',
  description:
   'Contacts and leads stay in sync across both CRMs automatically.',
  recommended: true,
 },
 {
  value: 'push',
  label: 'AgentRoof → Follow Up Boss',
  description: 'Push new leads from AgentRoof into Follow Up Boss only.',
 },
 {
  value: 'pull',
  label: 'Follow Up Boss → AgentRoof',
  description: 'Import contacts from Follow Up Boss into AgentRoof CRM.',
 },
]

export const AddFubIntergrationModal: FC<{
 editData?: FubData
 OnCancel?: () => void
 show?: boolean
 mode?: TMode
}> = ({ OnCancel, show, mode, editData }) => {
 const [connect, { isLoading: isConnecting }] = useFubConnectMutation()
 const [update, { isLoading: isUpdating }] = useFubUpdateMutation()

 const [form] = Form.useForm<TAddFubInput>()

 const handleSubmit = async () => {
  try {
   const value = await form.validateFields()
   const payload: TAddFubInput = {
    api_key: value.api_key,
    sync_direction: value.sync_direction,
   }
   if (mode === 'EDIT') {
    await update(payload).unwrap()
   } else {
    await connect(payload).unwrap()
   }
   OnCancel?.()
  } catch (error) {
   console.log(error)
  }
 }

 useEffect(() => {
  if (mode === 'EDIT' && editData) {
   // API key isn't returned by the status endpoint, so it must be re-entered to update.
   form.setFieldsValue({
    api_key: '',
    sync_direction: editData.sync_direction ?? 'twoway',
   })
  }
  return () => {
   form.resetFields()
  }
 }, [editData, mode])

 return (
  <Modal
   forceRender
   footer={null}
   onCancel={OnCancel}
   visible={show}
   width={520}
  >
   <div className="int-card-new-header mb-3">
    <img
     className="int-card-new-img int-card-new-img-fub"
     src={followupboss}
     alt=""
    />
   </div>
   <Form layout="vertical" form={form} initialValues={initialValues}>
    <Form.Item
     name="api_key"
     label="API Key"
     extra={
      mode === 'EDIT'
       ? 'Re-enter your API key to update settings. Find it under Admin → API in your Follow Up Boss account.'
       : 'Find your API key under Admin → API in your Follow Up Boss account.'
     }
     rules={[{ required: true, message: 'API Key is required' }]}
    >
     <Input
      type="password"
      autoComplete="new-password"
      placeholder="Paste your Follow Up Boss API key"
     />
    </Form.Item>
    <Form.Item
     name="sync_direction"
     label="Sync Direction"
     rules={[{ required: true, message: 'Sync direction is required' }]}
    >
     <Radio.Group className="fub-sync-group">
      {syncDirectionOptions.map((option) => (
       <Radio
        key={option.value}
        value={option.value}
        className="fub-sync-option"
       >
        <div className="fub-sync-option-title">
         {option.label}
         {option.recommended && (
          <span className="fub-sync-option-tag">Recommended</span>
         )}
        </div>
        <div className="fub-sync-option-desc">{option.description}</div>
       </Radio>
      ))}
     </Radio.Group>
    </Form.Item>
    <div style={{ marginTop: '25px' }}>
     <BaseButton
      block
      loading={isConnecting || isUpdating}
      onClick={handleSubmit}
      type="primary"
      style={{ height: 'auto', padding: '10px 0' }}
     >
      Update
     </BaseButton>
    </div>
   </Form>
  </Modal>
 )
}
